// src/components/header.tsx
"use client";
import Image from "next/image";
import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  clearCadastroProfile,
  hasCadastroSession as hasStoredCadastroSession,
} from "@/services/cadastroSession";
import {
  APP_STATE_STORAGE_EVENT,
  clearStoredState,
  getStoredState,
} from "@/services/stateStorage";

const HOME_PATHS = new Set(["/", "/home"]);
const HEADER_SELECTED_TAB_KEY = "header-v2-selected-tab";
const MENU_ROUTE_TO_TAB: Record<string, string> = {
  "/experiencias": "experiencias",
  "/fila": "fila",
  "/programacao": "programacao",
  "/atracoes": "atracoes",
  "/games": "games",
  "/ranking": "ranking",
};

export default function Header() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>("inicio");
  const [stateCode, setStateCode] = useState(getStoredState);
  const [hasCadastroSession, setHasCadastroSession] = useState(
    hasStoredCadastroSession,
  );

  const tabs = [
    {
      name: "Início",
      href: "/home",
      slug: "inicio",
    },
    {
      name: "Experiências",
      href: "/experiencias",
      slug: "experiencias",
    },
    {
      name: "Fila Virtual",
      href: "/filas/espera",
      slug: "filas",
    },
    {
      name: "Atrações",
      href: "/atracoes",
      slug: "atracoes",
    },
    {
      name: "Programação",
      href: "/programacao",
      slug: "programacao",
    },

    {
      name: "Games",
      href: "/jogos",
      slug: "jogos",
    },
    {
      name: "Ranking",
      href: "/ranking",
      slug: "ranking",
    },
  ];

  const visibleTabs = tabs.filter(
    (tab) =>
      !(
        (stateCode === "RJ" && tab.slug === "ranking") ||
        (stateCode === "SP" && tab.slug === "ranking") ||
        (stateCode === "RJ" && tab.slug === "filas") ||
        (stateCode !== "RJ" && tab.slug === "jogos")
      ),
  );

  const scrollToSection = (targetId: string, attempts = 0) => {
    const target = document.getElementById(targetId);

    if (!target) {
      if (attempts < 10) {
        window.setTimeout(() => {
          scrollToSection(targetId, attempts + 1);
        }, 120);
      }
      return;
    }

    const headerOffset = 96;
    const targetPosition =
      target.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.history.replaceState(null, "", `#${targetId}`);
    window.scrollTo({
      top: Math.max(targetPosition, 0),
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncSelectedTab = () => {
      const hash = window.location.hash.replace("#", "");
      const storedSelected = window.localStorage.getItem(
        HEADER_SELECTED_TAB_KEY,
      );

      const currentMenuTab = MENU_ROUTE_TO_TAB[pathname];

      if (currentMenuTab) {
        setSelected(currentMenuTab);
        window.localStorage.setItem(HEADER_SELECTED_TAB_KEY, currentMenuTab);
        return;
      }

      if (HOME_PATHS.has(pathname)) {
        if (hash === "programacao" || hash === "faq") {
          setSelected(hash);
          window.localStorage.setItem(HEADER_SELECTED_TAB_KEY, hash);
          return;
        }

        if (storedSelected === "programacao" || storedSelected === "faq") {
          setSelected(storedSelected);
          return;
        }

        setSelected("inicio");
        window.localStorage.setItem(HEADER_SELECTED_TAB_KEY, "inicio");
        return;
      }

      setSelected(null);
    };

    syncSelectedTab();
    window.addEventListener("hashchange", syncSelectedTab);

    return () => {
      window.removeEventListener("hashchange", syncSelectedTab);
    };
  }, [pathname]);

  useEffect(() => {
    const syncStorageState = () => {
      setStateCode(getStoredState());
      setHasCadastroSession(hasStoredCadastroSession());
    };

    window.addEventListener("storage", syncStorageState);
    window.addEventListener(APP_STATE_STORAGE_EVENT, syncStorageState);
    window.addEventListener("focus", syncStorageState);
    window.addEventListener("visibilitychange", syncStorageState);

    return () => {
      window.removeEventListener("storage", syncStorageState);
      window.removeEventListener(APP_STATE_STORAGE_EVENT, syncStorageState);
      window.removeEventListener("focus", syncStorageState);
      window.removeEventListener("visibilitychange", syncStorageState);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !HOME_PATHS.has(pathname)) return;

    const targetId = window.location.hash.replace("#", "");
    if (targetId !== "programacao" && targetId !== "faq") return;

    const timeoutId = window.setTimeout(() => {
      scrollToSection(targetId);
    }, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [pathname]);

  const handleMenuToggle = () => {
    setMenuOpen((currentState) => !currentState);
  };

  const handleTabSelection = (slug: string) => {
    setSelected(slug);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(HEADER_SELECTED_TAB_KEY, slug);
    }
  };

  async function handleCadastroLogout() {
    try {
      await fetch("/api/cadastro/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Best effort: even if the request fails, we still clear client state.
    } finally {
      clearCadastroProfile();
      setHasCadastroSession(false);
      setMenuOpen(false);
      window.location.replace("/cadastro");
    }
  }

  return (
    <>
      <header className="fixed z-10 grid grid-cols-3 items-center justify-items-center w-full h-26 px-[10px] bg-[#1E28DC] md:h-20 md:px-0">
        <div className="flex justify-self-start md:w-4 md:justify-center lg:pl-10 pl-1">
          <button
            type="button"
            name="menu-button"
            className="flex flex-col justify-center items-center gap-1 cursor-pointer"
            onClick={handleMenuToggle}
          >
            {!menuOpen ? (
              <div className="flex flex-col gap-1.5">
                <span className="w-8 h-0.5 bg-[#FEC822] md:w-6"></span>
                <span className="w-8 h-0.5 bg-[#FEC822] md:w-6"></span>
                <span className="w-8 h-0.5 bg-[#FEC822] md:w-6"></span>
              </div>
            ) : (
              <>
                <span className=" text-[#FEC822] text-[32px] md:text-[26px]">
                  X
                </span>
              </>
            )}
          </button>
        </div>

        <div className="flex justify-center items-center flex-col gap-1">
          <Image
            src="/logo.png"
            alt="Logo Casa CazéTV"
            width={130}
            height={90}
            className="cursor-pointer w-[150px] md:w-[120px]"
            onClick={() => {
              document.location.href = "/";
              clearStoredState();
            }}
            priority
          />
          {stateCode === "RJ" && pathname !== "/" && (
            <>
              <Image
                src="/assets/menu/rj.svg"
                alt="Rio de Janeiro"
                width={150}
                height={90}
                className="cursor-pointer w-[140px] md:w-[140px] mt-1"
                priority
              />
            </>
          )}
          {stateCode === "SP" && pathname !== "/" && (
            <>
              <Image
                src="/assets/menu/sp.svg"
                alt="São Paulo"
                width={150}
                height={90}
                className="cursor-pointer w-[140px] md:w-[140px] object-fit"
                priority
              />
            </>
          )}
        </div>

        <div className="flex justify-self-end gap-3 md:w-auto md:justify-center md:gap-4 lg:pr-10">
          <div className="lg:w-9 lg:h-9 w-8 h-8 bg-white rounded-full flex justify-center items-center cursor-pointer hover:bg-[#fec822] md:w-9 md:h-9">
            <Image
              src="/icons/instagram.svg"
              alt="Instagram"
              width={22}
              height={22}
              className="w-5 md:w-[22px]"
              onClick={() =>
                window.open("https://www.instagram.com/casacazetv/")
              }
              priority
            />
          </div>
          <div className="lg:w-9 lg:h-9 w-8 h-8 bg-white rounded-full flex justify-center items-center cursor-pointer hover:bg-[#fec822] md:w-9 md:h-9">
            <Image
              src="/icons/youtube.svg"
              alt="Youtube"
              width={22}
              height={22}
              className="w-5 md:w-[22px]"
              onClick={() => window.open("https://www.youtube.com/@CazeTV")}
              priority
            />
          </div>

          {/*<div className="lg:w-11 lg:h-11 w-8 h-8 bg-white rounded-full flex justify-center items-center cursor-pointer hover:bg-[#fec822] md:w-9 md:h-9">
            <Image
              src="/icons/mail.svg"
              alt="Youtube"
              width={22}
              height={22}
              className="w-4 md:w-[22px]"
              onClick={() => (document.location.href = "/")}
              priority
            />
          </div>*/}
        </div>
      </header>

      {pathname !== "/" ? (
        <>
          {/*bg-[#1E28DC] top-23 md:top-19 */}
          <div className="flex w-full h-12 bg-[#1E28DC] z-40 fixed top-23 md:top-19">
            <div
              className="scrollbar-hidden flex w-full flex-row gap-4 items-center justify-start lg:justify-center lg:ml-auto lg:mr-auto mb-4 overflow-x-auto overflow-y-hidden px-4 whitespace-nowrap"
              style={{
                marginBottom: -7,
                // position: "absolute",
                // bottom: 0,
              }}
            >
              {visibleTabs.map((x) => (
                <Link
                  key={x.slug}
                  href={x.href}
                  target="_self"
                  onClick={(event) => {
                    handleTabSelection(x.slug);

                    if (x.targetId && HOME_PATHS.has(pathname)) {
                      event.preventDefault();
                      scrollToSection(x.targetId);
                    }
                  }}
                >
                  <div
                    className="font-semibold shrink-0"
                    style={{
                      cursor: "pointer",
                      textAlign: "center",
                      color: selected === x.slug ? "#000" : "#fff",
                      fontWeight: "",
                      backgroundColor: selected === x.slug ? "#fec822" : "",
                      paddingTop: 5,
                      paddingBottom: 5,
                      padding: 6,
                      // borderRadius: 50,
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,

                      minWidth: 60,
                      fontSize: 14,
                      height: 40,
                    }}
                  >
                    <span>{x.name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {/* Container do Menu Mobile */}
      {menuOpen ? (
        <div className=" bg-[#1E28DC] flex flex-col items-center z-50 fixed top-24 md:top-20 w-full overflow-hidden">
          <Link href="/" className="font-bold text-[#FEC822] py-2">
            Escolha sua cidade
          </Link>

          <Link href="/cadastro" className="font-bold text-[#FEC822] py-2">
            Cadastre-se
          </Link>

          {/*<a href="/hospitality" className="font-bold text-[#FEC822] py-2">
            Hospitality Suites
          </a>*/}
          <a
            href="mailto:faleconosco@casacazetv.com.br"
            className="font-bold text-[#FEC822] py-2"
            onClick={() => setMenuOpen(false)}
          >
            Fale conosco
          </a>

          {hasCadastroSession && (
            <>
              <button
                type="button"
                className="font-bold text-black py-2 bg-[#FEC822] p-2 mb-2 rounded-[10px]"
                onClick={() => {
                  void handleCadastroLogout();
                }}
              >
                Sair
              </button>
            </>
          )}
        </div>
      ) : null}
    </>
  );
}
